import { normalizeProductVariants } from "./product-variant";
import { MAX_TEMPLATE_PHOTO_REFERENCES } from "./template-photo-references";
import { MAX_STANDARD_AD_REFERENCES, standardAdReferenceCapacityError } from "./ads-express-image-budget";
export { MAX_TEMPLATE_PHOTO_REFERENCES } from "./template-photo-references";
import {
  isAdsExpressImageEngineChoice,
  type AdsExpressImageEngineChoice,
} from "./ads-express-engine-selection";

// Pure builder for the Ads Express ad-generation request body.
//
// Extracted from the inline payload assembled in
// client/src/pages/ads-express.tsx (handleGenerate) so the selection->payload
// mapping — which decides product-vs-offer, saved-angle-vs-custom-concept, and
// how optional inputs (project, sales discount) are omitted — has a single,
// unit-testable source of truth. The server (POST /api/brands/:id/ad-generations)
// re-validates this shape with Zod and snapshots the resolved selections, so a
// regression here (e.g. sending an offerId in product mode, or dropping the
// trimmed custom concept) is exactly the class of bug that broke past runs.
// `server/__tests__/adGenerationRequest.test.ts` locks the mapping in.

// Task #812 — per-image reference purpose. "environment" = today's setting/
// mood steering (never copies the ref's product/people); "product" = the ref
// depicts a product that must be rendered faithfully as the ad's featured
// product; "auto" (default) = the server resolves the purpose at submit time
// from textual signals (note/context) or a lightweight vision classification.
// Task #1285 — "product_environment" (combined) = ONE reference drives BOTH
// channels: its pictured product is the ad's featured product AND its setting
// governs the ad's environment. Snapshots always store a RESOLVED purpose
// ("environment" | "product" | "product_environment") so a resume/restart
// never re-infers.
export type AdReferencePurpose =
  | "auto"
  | "environment"
  | "product"
  | "product_environment"
  | "person_identity"
  | "template_photo";

export const AD_REFERENCE_PURPOSES: readonly AdReferencePurpose[] = [
  "auto",
  "environment",
  "product",
  "product_environment",
  "person_identity",
  "template_photo",
];

// Brand-level, default-off gate for exact identity preservation. Keep this
// separate from reference_images_enabled: existing product/environment refs
// must not gain identity semantics accidentally.
export const AD_PERSON_IDENTITY_REFERENCES_FLAG_KEY = "ads_person_identity_references";
// Multi-reference ad renders otherwise underweight a tail identity image after
// the template and product references. Repeating the same consented source
// gives the renderer an explicit, deterministic identity channel.
export const AD_PERSON_IDENTITY_RENDER_REFERENCE_COPIES = 2 as const;

// A single visual reference image attached to an Ads Express run. `url` is the
// uploaded image (R2/`/uploads/`/http); `note` is the optional "what to pull
// from this" hint the user typed. `purpose` decides the prompt treatment:
// environment refs steer the ad's visual environment (background, lighting,
// color, mood) only, product refs make the depicted product appear as the
// ad's featured product, and "auto" defers the decision to submit time.
export interface AdReference {
  url: string;
  note: string;
  purpose: AdReferencePurpose;
  source?: "upload" | "product" | "adgen" | "offer";
  // Required for person_identity references. The server only snapshots this
  // purpose when the submitter explicitly confirms they have permission to
  // use the person's likeness.
  identityConsent?: boolean;
  // Task #812 follow-up — short factual caption of the FIRST product-purpose
  // reference, generated ONCE at submit time (GPT-4o-mini vision) and carried
  // on the snapshot. The Sim copywriter can't see the reference image, so
  // without this it authors the entire brief (including the mandatory
  // "Use ONLY this exact in-image copy" block) for the SELECTED product —
  // and that copy makes the image model draw the wrong product even when the
  // reference occupies the positional product slot. Absent on env refs and
  // legacy snapshots (no behaviour change).
  productDescription?: string | null;
  // Task #1307 (enhanced pipeline only) — anti-mimicry fields for ENVIRONMENT
  // references that contain prominent people. Text instructions alone cannot
  // stop an image model from cloning a person it is handed in a reference
  // photo, so at submit time a vision pass flags people-heavy env refs
  // (`hasPeople`) and captions the people's general vibe/casting
  // (`peopleVibe`); the render worker then derives a people-free "location
  // plate" from the photo (`cleanUrl`) and the image channel carries THAT
  // instead of the original. Only populated when the Ads Express enhanced
  // pipeline flag is ON at submit; absent on product refs, baseline runs, and
  // legacy snapshots (no behaviour change).
  hasPeople?: boolean | null;
  peopleVibe?: string | null;
  cleanUrl?: string | null;
  // Task #1645 — submit-time vision caption of the SPACE itself for
  // environment-channel references (environment and product_environment):
  // architecture, furniture with its colors/materials, tableware, lighting,
  // overall palette, and notable absences. The Sim copywriter used to receive
  // only "A real location — set the ad in this actual place", so it authored
  // scene direction blind and invented brand-colored props (green furniture /
  // green napkins). This caption grounds the text channel in closed-world
  // facts about the real space. Fail-open null; never re-captioned on
  // resume/retry; absent on product-only refs, baseline runs, and legacy
  // snapshots (which compose byte-identically to today).
  environmentDescription?: string | null;
}

// Task #1307 — the Ads Express output-pipeline variant. "baseline" (default)
// = the July 10 pipeline, byte-for-byte; "enhanced" = the post-July-10
// behavior (location plates, vision QA gates, band text QA, etc.), gated by
// the master-only ads_express_settings.enhanced_pipeline flag and snapshotted
// per run / per edit job.
export type AdPipelineVariant = "baseline" | "enhanced";

// Maximum number of visual references the UI allows / the server accepts.
export const MAX_AD_REFERENCES = 3;

// Task #1413 — maximum number of product photo-library images a standard run
// may carry as its product-reference override (mirrors the KC cap).
export const MAX_AD_PRODUCT_IMAGE_URLS = 20;

export type AdsExpressAttachmentSelection = {
  personaId: string;
  promotedItemType: "product" | "offer";
  productId?: string | null;
  offerId?: string | null;
};

export type AdsExpressNarrativeAngleAttachment = {
  personaId?: string | null;
  productId?: string | null;
  offeringId?: string | null;
  status?: string | null;
};

/**
 * One relationship contract for saved Ads Express angles. The browser uses it
 * to clear stale selections immediately; every server admission boundary uses
 * it as the authoritative pre-create check.
 */
export function narrativeAngleMatchesAdsExpressAttachment(
  angle: AdsExpressNarrativeAngleAttachment | null | undefined,
  selection: AdsExpressAttachmentSelection,
): boolean {
  if (!angle || angle.status === "archived" || angle.personaId !== selection.personaId) {
    return false;
  }
  return selection.promotedItemType === "offer"
    ? Boolean(selection.offerId) &&
        angle.offeringId === selection.offerId &&
        !angle.productId
    : Boolean(selection.productId) &&
        angle.productId === selection.productId &&
        !angle.offeringId;
}

export function productIdsWithSavedAngles(
  angles: readonly AdsExpressNarrativeAngleAttachment[],
  personaId: string | null | undefined,
): Set<string> {
  if (!personaId) return new Set();
  return new Set(angles.filter((angle) =>
    narrativeAngleMatchesAdsExpressAttachment(angle, {
      personaId, promotedItemType: "product", productId: angle.productId,
    }),
  ).map((angle) => angle.productId!));
}

export function offerIdsWithSavedAngles(
  angles: readonly AdsExpressNarrativeAngleAttachment[],
  personaId: string | null | undefined,
): Set<string> {
  if (!personaId) return new Set();
  return new Set(angles.filter((angle) =>
    narrativeAngleMatchesAdsExpressAttachment(angle, {
      personaId, promotedItemType: "offer", offerId: angle.offeringId,
    }),
  ).map((angle) => angle.offeringId!));
}

export function adsExpressAttachmentFormState(generation: {
  personaId?: string | null;
  promotedItemType?: string | null;
  productId?: string | null;
  offerId?: string | null;
  narrativeAngleId?: string | null;
}) {
  const offerMode = generation.promotedItemType === "offer" && Boolean(generation.offerId);
  return {
    personaId: generation.personaId ?? "",
    promotedItemType: offerMode ? "offer" as const : "product" as const,
    productId: offerMode ? "" : generation.productId ?? "",
    offerId: offerMode ? generation.offerId! : "",
    angleId: generation.narrativeAngleId ?? "",
  };
}

// Task #1413 — normalize the library-picked product image URLs (standard runs
// only): keep non-empty strings, trim, dedupe preserving click order, cap the
// count. Returns [] for anything empty/garbage — the caller treats [] as "no
// explicit selection" and stamps a null snapshot (legacy product-photo
// resolution), so an untouched run stays byte-for-byte identical.
export function normalizeAdProductImageUrls(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of raw) {
    const url = typeof u === "string" ? u.trim() : "";
    if (!url || seen.has(url)) continue;
    seen.add(url);
    out.push(url);
    if (out.length >= MAX_AD_PRODUCT_IMAGE_URLS) break;
  }
  return out;
}

// Single source of truth for normalizing the raw references list (used by both
// the client payload builder and the server Zod handler): keep only entries
// with a non-empty url, trim the note, and cap the count. Guarantees the
// additive-only, no-reference-path-unchanged contract — an empty/garbage list
// collapses to [].
export function normalizeAdReferences(
  raw:
    | ReadonlyArray<{ url?: unknown; note?: unknown; purpose?: unknown; source?: unknown; identityConsent?: unknown; productDescription?: unknown; hasPeople?: unknown; peopleVibe?: unknown; cleanUrl?: unknown; environmentDescription?: unknown }>
    | null
    | undefined,
  options?: { standardAdsExpress: true },
): AdReference[] {
  if (!Array.isArray(raw)) return [];
  if (options?.standardAdsExpress) {
    const error = standardAdReferenceCapacityError(raw.filter(r => typeof r?.url === "string" && r.url.trim()));
    if (error) throw new Error(error);
  }
  const out: AdReference[] = [];
  let ordinaryCount = 0;
  let templateCount = 0;
  for (const r of raw) {
    const url = typeof r?.url === "string" ? r.url.trim() : "";
    if (!url) continue;
    const note = typeof r?.note === "string" ? r.note.trim() : "";
    // Unknown/absent purpose collapses to "auto" (the safe default: the server
    // resolves it at submit time; legacy purpose-less snapshots downstream are
    // treated as environment, preserving today's behaviour).
    const purpose: AdReferencePurpose =
      r?.purpose === "environment" ||
      r?.purpose === "product" ||
      r?.purpose === "product_environment" ||
      r?.purpose === "person_identity" ||
      r?.purpose === "template_photo"
        ? r.purpose
        : "auto";
    const entry: AdReference = { url, note, purpose };
    if (r.source === "upload" || r.source === "product" || r.source === "adgen" || r.source === "offer") {
      entry.source = r.source;
    }
    if (purpose === "template_photo") {
      if (templateCount >= MAX_TEMPLATE_PHOTO_REFERENCES) continue;
      templateCount++;
    } else {
      if (ordinaryCount >= (options?.standardAdsExpress ? MAX_STANDARD_AD_REFERENCES : MAX_AD_REFERENCES)) continue;
      ordinaryCount++;
    }
    if (purpose === "person_identity" && r?.identityConsent === true) {
      entry.identityConsent = true;
    }
    // Preserve a submit-time product caption if one is already present (e.g.
    // when re-normalizing a snapshot); the client never sends this field.
    if (typeof r?.productDescription === "string" && r.productDescription.trim()) {
      entry.productDescription = r.productDescription.trim();
    }
    // Task #1307 (enhanced pipeline) — preserve submit-time people-analysis +
    // worker-time plate fields when re-normalizing a snapshot; the client
    // never sends these and baseline runs never populate them.
    if (typeof r?.hasPeople === "boolean") entry.hasPeople = r.hasPeople;
    if (typeof r?.peopleVibe === "string" && r.peopleVibe.trim()) {
      entry.peopleVibe = r.peopleVibe.trim();
    }
    if (typeof r?.cleanUrl === "string" && r.cleanUrl.trim()) {
      entry.cleanUrl = r.cleanUrl.trim();
    }
    // Task #1645 — preserve the submit-time space caption when re-normalizing
    // a snapshot; the client never sends this field.
    if (typeof r?.environmentDescription === "string" && r.environmentDescription.trim()) {
      entry.environmentDescription = r.environmentDescription.trim();
    }
    out.push(entry);
  }
  return out;
}

// ── Task #812: textual auto-purpose resolution ────────────────────────────────
// Strong textual signals in the per-image note (and, as a weaker fallback, the
// run-wide additional context) resolve an "auto" reference without a vision
// call. Returns null when the text is inconclusive — the caller then falls
// back to image classification (server) or environment (on failure).
// Pure + shared so the heuristic is unit-testable and identical everywhere.
const PRODUCT_SIGNAL_RE =
  /\b(use|put|show|feature|include|add|make|place|insert|sell)\b[^.!?\n]{0,60}\b(product|this|it|bottle|jar|can|box|pack|package|item)\b[^.!?\n]{0,40}\b(ad|ads|image|shot|creative|render|photo|picture)\b|\bproduct to feature\b|\bfeatured product\b|\bfeature (this|my|the|our) product\b|\buse (this|my|the|our) product\b|\bshow (this|my|the|our) product\b|\binclude (this|my|the|our) product\b|\bthis is (the|my|our) product\b|\bput this in\b|\bput it in\b/i;
const ENVIRONMENT_SIGNAL_RE =
  /\b(background|backdrop|setting|environment|scene|scenery|mood|lighting|light|location|room|vibe|atmosphere|aesthetic|tone|tones|palette|color scheme|texture|style of the (background|scene|setting))\b/i;

// Task #1285 — explicit "keep/reproduce the reference's setting" intent. This
// is deliberately STRICTER than ENVIRONMENT_SIGNAL_RE (a bare mention of
// "lighting" is NOT enough): it requires either a keep/preserve/match-style
// verb applied to an environment word, or a "product in this/the same setting"
// construction. Used two ways: (1) a product-signal note that ALSO carries
// this intent infers straight to the combined purpose; (2) an explicitly
// product-purpose reference whose note/run-context carries it upgrades to the
// combined purpose at submit time.
const ENVIRONMENT_KEEP_SIGNAL_RE =
  /\b(keep|keeps|keeping|preserve|preserves|preserving|maintain|maintains|maintaining|retain|retains|retaining|match|matches|matching|replicate|replicates|replicating|recreate|recreates|recreating|reproduce|reproduces|reproducing|copy|copies|copying|use|uses|using)\b[^.!?\n]{0,60}\b(same |exact |real )?(environment|setting|backdrop|scene|scenery|location|surroundings)\b|\b(in|within|inside)\s+(this|its|the same|that same|the exact)\s+(environment|setting|background|backdrop|scene|location|room|place)\b|\bsame (environment|setting|background|backdrop|scene|location) as\b/i;

// Does the reference's note (or, as a weaker fallback, the run-wide additional
// context) explicitly ask to keep the reference photo's setting? Pure + shared
// so the submit-time product→combined upgrade heuristic is unit-testable.
export function adReferenceTextRequestsEnvironment(
  note: string | null | undefined,
  additionalContext?: string | null,
): boolean {
  const n = typeof note === "string" ? note.trim() : "";
  if (n && ENVIRONMENT_KEEP_SIGNAL_RE.test(n)) return true;
  const ctx = typeof additionalContext === "string" ? additionalContext.trim() : "";
  return Boolean(ctx && ENVIRONMENT_KEEP_SIGNAL_RE.test(ctx));
}

export function inferAdReferencePurposeFromText(
  note: string | null | undefined,
  additionalContext?: string | null,
): "environment" | "product" | "product_environment" | null {
  const n = typeof note === "string" ? note.trim() : "";
  if (n) {
    // The per-image note is the strongest signal: check product intent first
    // ("put this in the ad" must win even if the note also mentions lighting).
    if (PRODUCT_SIGNAL_RE.test(n)) {
      // Task #1285 — a product note that ALSO explicitly asks to keep the
      // reference's setting ("show this product in this setting") resolves
      // to the combined purpose. A bare env-word mention is NOT enough.
      return ENVIRONMENT_KEEP_SIGNAL_RE.test(n) ? "product_environment" : "product";
    }
    if (ENVIRONMENT_SIGNAL_RE.test(n)) return "environment";
  }
  const ctx = typeof additionalContext === "string" ? additionalContext.trim() : "";
  if (ctx && PRODUCT_SIGNAL_RE.test(ctx)) return "product";
  return null;
}

// Normalize the freeform additional-context field: trim, collapse empty -> null.
export function normalizeAdditionalContext(
  raw: unknown,
): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// A reference image that has already been resolved + uploaded and appended to
// the generator's `image_urls` array. `slot` is its 1-based position in that
// array (e.g. 4/5/6 after example/logo/product); `note` is the optional user
// hint. `purpose` is the RESOLVED purpose (never "auto" — the route resolves
// autos at submit time); absent/unknown = "environment" so legacy purpose-less
// snapshots keep today's exact behaviour. Pure-data input to
// `composeReferenceInjectedPrompt`.
export interface ResolvedAdReference {
  slot: number;
  note?: string;
  // "product_environment" (Task #1285) = combined: the ref's product is the
  // featured product AND its setting governs the ad's environment.
  purpose?: "environment" | "product" | "product_environment" | "person_identity" | "template_photo";
  identityConsent?: boolean;
  // Submit-time vision caption of a product-purpose reference (what the photo
  // actually shows). Echoed into the render prompt as an anchor so the image
  // model can't reinterpret the pictured product as a different object.
  productDescription?: string | null;
  // Task #1307 (enhanced pipeline only) — submit-time vision caption of the
  // people in an ENVIRONMENT reference (their general vibe/casting — age
  // range, styling, energy, group size). When the render swaps the attached
  // image for a people-free location plate, this text keeps the vibe-matching
  // goal alive without giving the model a literal person to clone. Never set
  // on baseline runs.
  peopleVibe?: string | null;
  // Task #1645 — submit-time vision caption of the real SPACE in an
  // environment-channel reference (environment / product_environment):
  // architecture, furniture colors/materials, tableware, lighting, palette,
  // notable absences. Folded into the guidance line with closed-world
  // anti-invention rules so the Sim copywriter stops inventing decor and
  // extending brand colors onto physical scene objects. Absent/null (all
  // legacy snapshots + baseline runs) composes today's exact line.
  environmentDescription?: string | null;
}

/**
 * Build the resolved reference metadata used by both the pre-Sim slot
 * prediction and the post-upload renderer boundary.
 *
 * Keeping this conversion shared prevents consent from being present in the
 * submit snapshot but accidentally dropped before exact-identity enforcement.
 */
export function buildResolvedAdReference(args: {
  slot: number;
  purpose: NonNullable<ResolvedAdReference["purpose"]>;
  reference: {
    note?: unknown;
    identityConsent?: unknown;
    productDescription?: unknown;
    peopleVibe?: unknown;
    environmentDescription?: unknown;
  };
}): ResolvedAdReference {
  const { slot, purpose, reference } = args;
  return {
    slot,
    note: typeof reference.note === "string" ? reference.note.trim() : "",
    purpose,
    identityConsent:
      purpose === "person_identity" && reference.identityConsent === true
        ? true
        : undefined,
    productDescription:
      purpose === "product" || purpose === "product_environment"
        ? ((reference.productDescription as string | null | undefined) ?? null)
        : null,
    peopleVibe:
      purpose === "environment" &&
      typeof reference.peopleVibe === "string" &&
      reference.peopleVibe.trim()
        ? reference.peopleVibe.trim()
        : null,
    environmentDescription:
      (purpose === "environment" || purpose === "product_environment") &&
      typeof reference.environmentDescription === "string" &&
      reference.environmentDescription.trim()
        ? reference.environmentDescription.trim()
        : null,
  };
}

export function buildResolvedAdReferenceCopies(args: {
  startSlot: number;
  purpose: NonNullable<ResolvedAdReference["purpose"]>;
  reference: Parameters<typeof buildResolvedAdReference>[0]["reference"];
}): ResolvedAdReference[] {
  const copies =
    args.purpose === "person_identity"
      ? AD_PERSON_IDENTITY_RENDER_REFERENCE_COPIES
      : 1;
  return Array.from({ length: copies }, (_, index) =>
    buildResolvedAdReference({
      slot: args.startSlot + index + 1,
      purpose: args.purpose,
      reference: args.reference,
    }),
  );
}

export function planAdsExpressTailReferenceSlots(args: {
  startSlot: number;
  includePersonaCharacterSheet?: boolean;
  adIdentityPackCount?: number;
  references: ReadonlyArray<{
    purpose: NonNullable<ResolvedAdReference["purpose"]>;
    reference: Parameters<typeof buildResolvedAdReference>[0]["reference"];
  }>;
}): {
  personaCharacterSheetSlot: number | null;
  adIdentityPackSlots: number[];
  references: ResolvedAdReference[];
  finalSlot: number;
} {
  let cursor = args.startSlot;
  const personaCharacterSheetSlot = args.includePersonaCharacterSheet
    ? ++cursor
    : null;
  const adIdentityPackSlots = Array.from(
    { length: args.adIdentityPackCount === 3 ? 3 : 0 },
    () => ++cursor,
  );
  const references: ResolvedAdReference[] = [];
  for (const item of args.references) {
    const copies = buildResolvedAdReferenceCopies({
      startSlot: cursor,
      purpose: item.purpose,
      reference: item.reference,
    });
    references.push(...copies);
    cursor += copies.length;
  }
  return { personaCharacterSheetSlot, adIdentityPackSlots, references, finalSlot: cursor };
}

export function personIdentityRendererActive(args: {
  engineContext?: string | null;
  isMeme: boolean;
  references: ReadonlyArray<{
    url?: unknown;
    purpose?: unknown;
    identityConsent?: unknown;
  }>;
  identityReferenceUrls?: ReadonlyArray<string> | null;
}): boolean {
  if (args.engineContext !== "ads_express" || args.isMeme) return false;
  const explicitUrls = Array.isArray(args.identityReferenceUrls)
    ? new Set(args.identityReferenceUrls.map((url) => url.trim()).filter(Boolean))
    : null;
  return args.references.some((reference) => {
    const url =
      typeof reference.url === "string" ? reference.url.trim() : "";
    return (
      Boolean(url) &&
      reference.purpose === "person_identity" &&
      reference.identityConsent === true &&
      (explicitUrls === null || explicitUrls.has(url))
    );
  });
}

export function resolveIdentityAwareImageEngineOverride(args: {
  personIdentityActive: boolean;
  isMeme: boolean;
  overrideScopedOut: boolean;
  imageEngineOverrideSnapshot?: "gpt-image-2" | "flux-2-pro" | null;
}): "gpt-image-2" | "flux-2-pro" | null {
  if (args.personIdentityActive) return "gpt-image-2";
  if (args.isMeme || args.overrideScopedOut) return null;
  return args.imageEngineOverrideSnapshot ?? null;
}

export function composePersonIdentityPersonaInfo(args: {
  basePersonaInfo: string;
  identityActive: boolean;
}): string {
  if (!args.identityActive) return args.basePersonaInfo;
  const directive =
    "PERSON / IDENTITY CASTING OVERRIDE — the persona data describes the TARGET AUDIENCE and messaging strategy only; it does NOT describe the visible model. Do not derive the model's name, face, body, age, gender presentation, hair, styling, or any other physical trait from the persona. The attached PERSON / IDENTITY reference is the only source of truth for the visible person's identity and appearance.";
  return args.basePersonaInfo
    ? `${args.basePersonaInfo} | ${directive}`
    : directive;
}

// Human label for a possibly non-contiguous set of image slots:
// [4] -> "Image 4"; [4,6] -> "Images 4 and 6"; [4,5,6] -> "Images 4, 5 and 6".
function labelForSlots(slots: ReadonlyArray<number>): string {
  if (slots.length === 1) return `Image ${slots[0]}`;
  if (slots.length === 2) return `Images ${slots[0]} and ${slots[1]}`;
  return `Images ${slots.slice(0, -1).join(", ")} and ${slots[slots.length - 1]}`;
}

// Task #1331 follow-up — compose the `referenceGuidance` Start input for the
// user-reference Sim workflow pair (the beta "New NB Prompt Maker w/ refs"
// clones). One line per user reference, addressed by its PREDICTED 1-based
// positional image number, so the copywriter can author scene/product text
// that already agrees with the attached references instead of writing blind.
// PURE + single source of truth: the generator predicts the slots before the
// Sim call, and the same composer re-runs on the ACTUAL resolved refs after
// the Fal uploads as an alignment tripwire.
// Task #1392 — `additionalContext` is the run's freeform "Additional context"
// text from Advanced settings. On reference runs the server no longer appends
// it to the Fal prompt itself; instead it rides here as a clearly labeled
// final line so the Sim copywriter (which owns ALL prompt text on ref runs)
// can fold it in. Null/empty = no extra line (byte-identical guidance).
export function composeUserReferenceGuidance(
  refs: ReadonlyArray<ResolvedAdReference>,
  additionalContext?: string | null,
): string {
  if (!Array.isArray(refs) || refs.length === 0) return "";
  const lines: string[] = [];
  for (const ref of refs) {
    const note = typeof ref.note === "string" ? ref.note.trim() : "";
    const productDesc =
      typeof ref.productDescription === "string" ? ref.productDescription.trim() : "";
    const vibe = typeof ref.peopleVibe === "string" ? ref.peopleVibe.trim() : "";
    // Task #1645 — closed-world grounding for environment-channel refs. Only
    // composed when the submit-time space caption exists; caption-less
    // entries (legacy snapshots, baseline runs, caption failures) compose
    // today's exact lines byte-for-byte.
    const envFacts =
      typeof ref.environmentDescription === "string" ? ref.environmentDescription.trim() : "";
    const envFactParts = envFacts
      ? [
          `REAL-SPACE FACTS (vision-extracted from this exact photo — treat as the complete inventory of the space): ${envFacts}`,
          "Reproduce this real space exactly as described: do NOT invent, add, or swap furnishings, fixtures, tableware, decor, or props that are not in these facts, and do NOT recolor or restyle the space's physical objects — brand colors belong ONLY on typography, CTA elements, and graphic overlays, never on furniture, walls, or objects in the scene.",
        ]
      : [];
    if (ref.purpose === "person_identity") {
      const parts: string[] = [
        "Preserve the same identifiable person shown in this image.",
        "This is an exact identity reference, not general appearance, vibe, styling, or environment guidance.",
        "Do not substitute a different person, do not infer or add sensitive personal traits, and do not copy the reference photo's product, composition, or background.",
      ];
      if (note) parts.push(`The user's identity note: "${note}".`);
      lines.push(`Image ${ref.slot} (PERSON / IDENTITY reference): ${parts.join(" ")}`);
    } else if (ref.purpose === "product") {
      const parts: string[] = [
        productDesc ||
          "The actual product this ad must feature — match its real appearance (shape, label, colors, packaging) exactly as photographed.",
      ];
      if (note) parts.push(`The user's own note about this product: "${note}".`);
      lines.push(`Image ${ref.slot} (PRODUCT reference): ${parts.join(" ")}`);
    } else if (ref.purpose === "product_environment") {
      const parts: string[] = [
        productDesc ||
          "Shows the actual product this ad must feature IN the real location the ad should be set in — match the product's appearance exactly and reproduce the pictured place as the setting.",
      ];
      if (note) parts.push(`The user's own note: "${note}".`);
      parts.push(...envFactParts);
      if (vibe)
        parts.push(
          `People in the reference (text description only — cast different people with this general vibe, never a specific individual): ${vibe}`,
        );
      lines.push(`Image ${ref.slot} (PRODUCT + ENVIRONMENT/STYLE reference): ${parts.join(" ")}`);
    } else {
      const parts: string[] = [
        note || "A real location — set the ad in this actual place.",
      ];
      parts.push(...envFactParts);
      if (vibe)
        parts.push(
          `People in the reference (text description only — cast different people with this general vibe, never a specific individual): ${vibe}`,
        );
      lines.push(`Image ${ref.slot} (ENVIRONMENT/STYLE reference): ${parts.join(" ")}`);
    }
  }
  const extraContext =
    typeof additionalContext === "string" ? additionalContext.trim() : "";
  if (extraContext) {
    lines.push(
      `ADDITIONAL CONTEXT (the user's own freeform instructions for this run — honor them in the prompt you write): ${extraContext}`,
    );
  }
  return lines.join("\n");
}

// Compose the final ad prompt with visual-reference steering. PURE + the single
// source of truth for the reference text channel, so it can be unit-tested
// without the generator's Fal/Sim/DB I/O.
//
// Contract:
//   - No resolved references -> returns `basePrompt` UNCHANGED (guarantees the
//     no-reference path is byte-for-byte identical to legacy output).
//   - Environment refs -> PREPEND the authoritative "ENVIRONMENT OVERRIDE"
//     preamble (so it outranks the Sim prompt's own competing background
//     description, which gpt-image-2 otherwise follows while ignoring a weak
//     trailing "match these references" line), keep `basePrompt` in the middle,
//     and append a per-slot reference map (first env slot = PRIMARY environment,
//     the rest supporting) plus the optional additional-context line. An
//     all-environment set (including legacy purpose-less snapshots) produces
//     byte-for-byte the same output as before purposes existed.
//   - Product refs (Task #812) -> PREPEND a "FEATURED PRODUCT OVERRIDE"
//     preamble instructing the model to render the referenced product
//     faithfully as the ad's featured product (shape/label/colors), while
//     layout/copy still come from the Sim prompt.
//   - Mixed sets -> both preambles (product first), each scoped to its own
//     slot labels, plus per-slot role lines at the tail.
//
// Environment refs steer the visual ENVIRONMENT only; their preamble fences
// off copying the references' people/products/text/logos/layout so product,
// copy, and layout still come entirely from the Sim prompt + positional refs.
export function composeReferenceInjectedPrompt(args: {
  basePrompt: string;
  refs: ReadonlyArray<ResolvedAdReference>;
  additionalContext?: string | null;
  // Task #1307 — output-pipeline variant. Absent/"baseline" (default) keeps
  // the July 10 prompt wording byte-for-byte; "enhanced" selects the
  // post-July-10 wording (reproduce-the-real-place env preamble, people
  // vibe-matching clauses). The two wordings are WHOLE separate string
  // constants — never interleaved conditionals — so the baseline path is
  // provably unchanged.
  variant?: AdPipelineVariant;
}): string {
  const { basePrompt, refs } = args;
  const enhanced = args.variant === "enhanced";
  if (!Array.isArray(refs) || refs.length === 0) return basePrompt;

  // Task #812 — split by resolved purpose. Absent/unknown purpose = environment
  // (legacy purpose-less snapshots keep today's exact output).
  // Task #1285 — a combined ("product_environment") ref participates in BOTH
  // channels: it is a product ref AND an environment ref. Within the env
  // channel, PURE environment refs outrank combined ones — the first pure env
  // ref stays PRIMARY; a combined ref is primary only when no pure env ref
  // exists. Sets without combined refs are byte-for-byte unchanged.
  const isCombined = (r: ResolvedAdReference) => r.purpose === "product_environment";
  const identityRefs = refs.filter((r) => r.purpose === "person_identity");
  const productRefs = refs.filter((r) => r.purpose === "product" || isCombined(r));
  const pureEnvRefs = refs.filter(
    (r) => r.purpose !== "product" && !isCombined(r) && r.purpose !== "person_identity",
  );
  const combinedRefs = refs.filter(isCombined);
  const envRefs = [...pureEnvRefs, ...combinedRefs];

  const noteClauseFor = (ref: ResolvedAdReference): string => {
    const note = typeof ref.note === "string" ? ref.note.trim() : "";
    return note ? ` [User note: ${note}]` : "";
  };

  const identityLabel = labelForSlots(identityRefs.map((r) => r.slot).sort((a, b) => a - b));
  const identityPreamble = identityRefs.length > 0
    ? `PERSON / IDENTITY REFERENCE (EXACT IDENTITY, HIGHEST PRIORITY FOR THE PERSON): ${identityLabel} contains the reference image(s) of the person whose identifiable identity must be preserved in the generated ad. Use these images only to keep that same person recognizable; this is NOT a request to copy a generic appearance, vibe, wardrobe, environment, pose, or photographic composition. The persona describes the target audience, NOT the visible model. Ignore every conflicting person description, pronoun, gender label, age, face, hair, body, or casting detail in the main prompt, persona, layout example, or template; the visible person's identity and physical appearance must come only from ${identityLabel}. Never substitute a different woman, man, model, or customer. Do not treat these images as product references, do not copy their background or readable text, and do not infer or add sensitive traits. The user confirmed they have permission to use this person's likeness. If the requested template cannot safely preserve a recognizable person, fail with an actionable limitation rather than silently substituting another person.`
    : "";

  const identityRefLines = identityRefs.map((ref) => {
    const note = typeof ref.note === "string" ? ref.note.trim() : "";
    return `PERSON / IDENTITY REFERENCE — Image ${ref.slot}: preserve the same identifiable person from this image in the ad. Match identity, not merely general appearance or vibe; use a new composition and do not copy the reference background, product, text, logo, pose, or camera framing.${note ? ` [User note: ${note}]` : ""}`;
  });

  // Task #1307 (enhanced pipeline) — environment channel: reproduce the actual
  // place + match people vibe. When a people-heavy reference photo was swapped
  // for a people-free location plate at render time, `peopleVibe` carries the
  // original people's vibe/casting as TEXT — the model gets the place from the
  // image and the people direction from words, with no literal person
  // available to clone. Baseline runs never set peopleVibe, and the clause is
  // additionally hard-gated on the enhanced variant.
  const vibeClauseFor = (ref: ResolvedAdReference): string => {
    if (!enhanced) return "";
    const vibe = typeof ref.peopleVibe === "string" ? ref.peopleVibe.trim() : "";
    return vibe
      ? ` The people who belong in this place (text description only — cast the ad's OWN, entirely different subject(s) to match this general vibe, never a specific individual): ${vibe}`
      : "";
  };

  // Task #1285 — a combined ref's env line must NOT fence off its own product
  // or label text (the product channel commands reproducing them); only the
  // combined refs get the reworded lines, everything else stays byte-identical.
  // Task #1307 — each line exists as TWO whole-string constants: the baseline
  // wording (July 10, byte-for-byte) and the enhanced wording (reproduce the
  // real place, vibe-match people). Selected wholesale by variant.
  const envRefLines = enhanced
    ? envRefs.map((ref, i) =>
        isCombined(ref)
          ? i === 0
            ? `VISUAL REFERENCE — Image ${ref.slot} (PRIMARY environment — same image as the featured product reference): this is the actual place the scene is set in — reproduce its real location, structure and spatial arrangement, background surfaces, fixtures, lighting, color temperature, texture, depth, and spatial mood so the ad is recognizably set in this specific place. Treat it as the LOCATION for a brand-new shot, NOT as a photograph to re-create: do not reuse its composition, camera angle, framing, or the specific person, pose, or moment it captures. If it shows people, match their general vibe and casting (approximate age range, styling/wardrobe, energy, mood, group size) in the ad's OWN subject, but do NOT clone any specific face, identity, or body. This same image also shows the featured product (see the PRODUCT REFERENCE line above) — reproducing that product and its own label IS required; apart from the featured product's own label and packaging, do NOT copy other readable text, logos, or signs from this image, and do NOT produce a collage or split-screen.${vibeClauseFor(ref)}${noteClauseFor(ref)}`
            : `VISUAL REFERENCE — Image ${ref.slot} (supporting — same image as a featured product reference): pull supporting location detail, lighting, color palette, and mood from this image; if it shows people, match only their general vibe and casting. Do NOT clone specific faces or identities. Reproducing its featured product and that product's own label IS required (see the PRODUCT REFERENCE line above); apart from that, do NOT copy other readable text, logos, or signs.${vibeClauseFor(ref)}${noteClauseFor(ref)}`
          : i === 0
          ? `VISUAL REFERENCE — Image ${ref.slot} (PRIMARY environment): this is the actual place the scene is set in — reproduce its real location, structure and spatial arrangement, background surfaces, fixtures, lighting, color temperature, texture, depth, and spatial mood so the ad is recognizably set in this specific place. Treat it as the LOCATION for a brand-new shot, NOT as a photograph to re-create: do not reuse its composition, camera angle, framing, or the specific person, pose, or moment it captures. If it shows people, match their general vibe and casting (approximate age range, styling/wardrobe, energy, mood, group size) in the ad's OWN subject, but do NOT clone any specific face, identity, or body. Do NOT copy its readable text, logos, or signs, and do NOT produce a collage or split-screen.${vibeClauseFor(ref)}${noteClauseFor(ref)}`
          : `VISUAL REFERENCE — Image ${ref.slot} (supporting): pull supporting location detail, lighting, color palette, and mood from this image; if it shows people, match only their general vibe and casting. Do NOT clone specific faces or identities, and do NOT copy its readable text, logos, or signs.${vibeClauseFor(ref)}${noteClauseFor(ref)}`,
      )
    : envRefs.map((ref, i) =>
        isCombined(ref)
          ? i === 0
            ? `VISUAL REFERENCE — Image ${ref.slot} (PRIMARY environment — same image as the featured product reference): this is the main setting/background the scene is placed in — match its location, background surfaces, lighting, color temperature, texture, depth, and spatial mood. This same image also shows the featured product (see the PRODUCT REFERENCE line above) — reproducing that product and its own label IS required; apart from the featured product's own label and packaging, do NOT copy its people, other text, logos, signs, or layout.${noteClauseFor(ref)}`
            : `VISUAL REFERENCE — Image ${ref.slot} (supporting — same image as a featured product reference): pull only supporting lighting, color palette, and mood from this image. Reproducing its featured product and that product's own label IS required (see the PRODUCT REFERENCE line above); apart from that, do NOT copy its people, other text, logos, signs, or layout.${noteClauseFor(ref)}`
          : i === 0
          ? `VISUAL REFERENCE — Image ${ref.slot} (PRIMARY environment): this is the main setting/background the scene is placed in — match its location, background surfaces, lighting, color temperature, texture, depth, and spatial mood. Do NOT copy its people, products, text, logos, signs, or layout.${noteClauseFor(ref)}`
          : `VISUAL REFERENCE — Image ${ref.slot} (supporting): pull only supporting lighting, color palette, and mood from this image. Do NOT copy its people, products, text, logos, signs, or layout.${noteClauseFor(ref)}`,
      );

  // Task #1307 — the env preamble likewise exists as two WHOLE constants:
  // baseline = the July 10 "SETTING ONLY" wording (byte-for-byte); enhanced =
  // the "reproduce the actual place + match people vibe" wording.
  const buildEnvPreamble = (rangeLabel: string, primaryClause: string): string =>
    enhanced
      ? `ENVIRONMENT OVERRIDE — SETTING (HIGHEST PRIORITY): The visual reference image(s) below (${rangeLabel}) are the source of truth for the ad's setting and location. Place this ad in the ACTUAL, recognizable place shown in ${rangeLabel}: reproduce its real location, structure and spatial arrangement, architecture, furniture and fixture placement, background surfaces and materials, lighting direction and quality, color temperature, texture, depth, and overall mood, so a viewer recognizes it as that specific place — not merely a vaguely similar vibe. Wherever the main prompt below describes a background, room, setting, location, scenery, lighting, or atmosphere, treat those words as placeholders and REPLACE that environment with the real place from ${rangeLabel}.${primaryClause} This applies to EVERY ad style with NO exception: even if the main prompt calls for a studio shot, a seamless or solid-color backdrop, a plain/gradient sweep, a "clean studio" look, or a bare tabletop/product-only set, you MUST discard that generic backdrop and rebuild the scene inside the real-world location from ${rangeLabel} — never keep a blank, seamless, or studio background when a reference environment is provided. If ${rangeLabel} shows people, MATCH THEIR VIBE AND CASTING in the ad's own subject(s): their approximate age range, gender mix, wardrobe and styling, grooming, energy, mood and expression, and group size, so the people read as genuinely belonging in that place — but do NOT literally copy, clone, or reproduce any specific face, identity, or body from ${rangeLabel}, and do NOT produce a collage, split-screen, or side-by-side; render one cohesive scene with the ad's own subject. CRITICALLY — do NOT re-stage, re-create, or closely imitate the reference photograph itself. ${rangeLabel} is the LOCATION the ad is shot in, not the shot to reproduce: do not reuse the reference's composition, camera angle, framing, or the specific moment it captures, and do not render a person who reads as the SAME individual seen in ${rangeLabel} doing the same thing in the same spot. The ad is a NEW photograph of the ad's OWN subject, staged and posed per the main prompt below, that simply takes place inside this location. Keep EVERYTHING ELSE from the main prompt exactly as specified: the subject/person and their action, pose, and expression, the product and its placement, the ad-unit layout and composition, the crop, any card/graphic/text structure, and every piece of in-image copy. If the subject's described action implies a particular room or location (for example a mirror selfie implying a bathroom, or a kitchen, desk, gym, or car scene), KEEP the person, their action, pose, expression, framing, and the product, but RELOCATE that moment into the real place from ${rangeLabel}: the implied room or location MUST change to the reference location, and you may adapt only incidental props (e.g. swap a bathroom mirror for a believable equivalent, or drop it) so the person reads as genuinely present in the reference location. Do NOT copy any readable text, logos, signs, brand marks, or watermarks FROM ${rangeLabel}, and do NOT clone specific faces or identities — reproduce the PLACE faithfully and MATCH the people's vibe, then stage the main prompt's own subject, product, and copy naturally inside it as a fresh, distinct photograph taken in that place — never a copy of the reference shot.`
      : `ENVIRONMENT OVERRIDE — SETTING ONLY (HIGHEST PRIORITY): The visual reference image(s) below (${rangeLabel}) are the source of truth for the ad's surrounding environment ONLY: the location and background, surface materials, lighting direction and quality, color temperature, texture, depth, and overall mood. Wherever the main prompt below describes a background, room, setting, location, scenery, lighting, or atmosphere, treat those words as placeholders and REPLACE that environment with ${rangeLabel}.${primaryClause} This applies to EVERY ad style with NO exception: even if the main prompt calls for a studio shot, a seamless or solid-color backdrop, a plain/gradient sweep, a "clean studio" look, or a bare tabletop/product-only set, you MUST discard that generic backdrop and rebuild the scene inside the real-world environment from ${rangeLabel} — never keep a blank, seamless, or studio background when a reference environment is provided. Keep EVERYTHING ELSE from the main prompt exactly as specified: the subject/person and their action, pose, and expression, the product and its placement, the ad-unit layout and composition, the crop, any card/graphic/text structure, and every piece of in-image copy. If the subject's described action implies a particular room or location (for example a mirror selfie implying a bathroom, or a kitchen, desk, gym, or car scene), KEEP the person, their action, pose, expression, framing, and the product, but RELOCATE that moment into the environment from ${rangeLabel}: the implied room or location MUST change to the reference setting, and you may adapt only incidental props (e.g. swap a bathroom mirror for a believable equivalent, or drop it) so the person reads as genuinely present in the reference location. Do NOT copy any people, faces, bodies, products, logos, signs, readable text, object arrangements, or layouts FROM ${rangeLabel} — pull ONLY the environment from them, and place the existing subject, product, and copy naturally into that environment as if photographed there. Do not produce a collage or split-screen.`;

  let envPreamble = "";
  if (envRefs.length > 0) {
    const firstSlot = envRefs[0].slot;
    const lastSlot = envRefs[envRefs.length - 1].slot;
    // Legacy env-only runs keep the exact "Images 4-6" range label; mixed sets
    // (where env slots may be non-contiguous) use an explicit slot list.
    // Task #1285 — env slots may be listed out of numeric order (pure env refs
    // outrank combined ones), so sort the label's slots ascending for readability.
    const rangeLabel =
      productRefs.length === 0
        ? envRefs.length === 1
          ? `Image ${firstSlot}`
          : `Images ${firstSlot}-${lastSlot}`
        : labelForSlots(envRefs.map((r) => r.slot).sort((a, b) => a - b));
    const primaryClause =
      envRefs.length > 1
        ? ` Use Image ${firstSlot} as the primary setting; use the remaining references only for supporting lighting, palette, and mood unless a per-image note says otherwise.`
        : "";
    envPreamble = buildEnvPreamble(rangeLabel, primaryClause);
    // Task #1285 — when a combined ref contributes to the env channel, exempt
    // its OWN featured product from the preamble's copy fences. STRICTLY
    // ADDITIVE: absent combined refs, the preamble is byte-for-byte unchanged.
    if (combinedRefs.length > 0) {
      const combinedLabel = labelForSlots(
        combinedRefs.map((r) => r.slot).sort((a, b) => a - b),
      );
      envPreamble = `${envPreamble} COMBINED REFERENCE NOTE: ${combinedLabel} also supplies the ad's FEATURED PRODUCT (see the FEATURED PRODUCT OVERRIDE above) — reproduce BOTH its setting AND its pictured product; the rules above against copying products, readable text, or logos from the reference do NOT apply to that featured product and its own label.`;
    }
  }

  // ── Product channel (Task #812): refs whose depicted product must appear ──
  let productPreamble = "";
  let productRefLines: string[] = [];
  if (productRefs.length > 0) {
    const prodLabel = labelForSlots(productRefs.map((r) => r.slot));
    const plural = productRefs.length > 1;
    productRefLines = productRefs.map((ref) => {
      const desc =
        typeof ref.productDescription === "string" ? ref.productDescription.trim() : "";
      const descClause = desc ? ` For clarity, Image ${ref.slot} shows: ${desc}.` : "";
      // Task #1285 — for a combined ref the setting IS also to be reproduced
      // (governed by the ENVIRONMENT OVERRIDE), so its line must not fence off
      // the background; pure product refs keep the exact legacy wording.
      const backgroundClause = isCombined(ref)
        ? ` This image's setting is ALSO the ad's environment — reproduce the place it shows per the ENVIRONMENT OVERRIDE, and render the featured product naturally within that setting (do not copy this photo's exact composition, camera angle, or framing).`
        : ` Do NOT copy this image's background, scene, lighting, or people — pull ONLY the product itself.`;
      return `PRODUCT REFERENCE — Image ${ref.slot} (featured product): this exact product MUST appear in the ad as the featured product — reproduce its true shape, proportions, packaging, materials, label design, label text, logos, and colors faithfully. If it is an unpackaged item such as a drink or plated food, reproduce the exact glass or vessel, liquid color, foam and layering, and garnish, and do NOT substitute a generic or different-looking version.${descClause}${backgroundClause} Do NOT copy this image's focus, depth of field, blur, bokeh, softness, grain, or other camera characteristics either — even if the product appears partly out of focus in the reference, render the product tack-sharp and fully in focus in the ad, unless the main prompt explicitly asks for a soft-focus style.${noteClauseFor(ref)}`;
    });
    productPreamble =
      `FEATURED PRODUCT OVERRIDE (HIGHEST PRIORITY): The product reference image${plural ? "s" : ""} (${prodLabel}) show${plural ? "" : "s"} the exact product${plural ? "s" : ""} that MUST appear in this ad as the featured product${plural ? "s" : ""}. Render ${plural ? "each of them" : "it"} faithfully and prominently: reproduce the true shape, proportions, packaging, materials, label design, label text, logos, and colors exactly as shown in ${prodLabel} — never restyle, recolor, rename, redesign, or replace ${plural ? "them" : "it"} with a generic or different product. Wherever the main prompt below describes, shows, or references the featured/hero product, that product IS the one shown in ${prodLabel}; if the main prompt describes a different product, the product from ${prodLabel} takes priority and replaces it while keeping the described placement, scale, and role in the composition. IGNORE any physical description of a different product in the main prompt — its container or packaging type, shape, materials, label design, or label text: the featured product's entire appearance comes ONLY from ${prodLabel}, and the main prompt's own product must NOT appear anywhere in the ad. If the product shown in ${prodLabel} has no packaging or label (for example a prepared drink, a plated food, or another unpackaged item), depict it exactly as it appears — do NOT invent a jar, bottle, box, wrapper, container, or label for it, and do NOT swap it for a packaged product, even if the ad copy talks about labels, packaging, or ingredient lists. Keep EVERYTHING ELSE from the main prompt exactly as specified: the ad-unit layout and composition, the crop, any card/graphic/text structure, every piece of in-image copy, any people and their actions, and the background/setting${envRefs.length > 0 ? " (which is separately governed by the environment reference images below)" : ""}. Do NOT copy the background, scene, lighting, or any people FROM ${prodLabel} — pull ONLY the product${plural ? "s" : ""}. Copy ONLY the product's identity, NEVER the reference photo's photographic qualities: do NOT copy its focus, depth of field, blur, bokeh, softness, grain, or other camera characteristics — even if the product appears partly or fully out of focus in ${prodLabel}, render the product tack-sharp, crisp, and fully in focus in the ad, unless the main prompt explicitly asks for a soft-focus style. Do not produce a collage or split-screen.`;
    // Task #1285 — combined refs are an explicit exception to the preamble's
    // "do not copy the background FROM the product reference" fence: their
    // setting is ALSO the ad's environment. STRICTLY ADDITIVE — sets without
    // combined refs keep the preamble byte-for-byte unchanged.
    if (combinedRefs.length > 0) {
      const combinedLabel = labelForSlots(
        combinedRefs.map((r) => r.slot).sort((a, b) => a - b),
      );
      productPreamble = `${productPreamble} EXCEPTION — ${combinedLabel} ${combinedRefs.length > 1 ? "are combined product + environment references" : "is a combined product + environment reference"}: unlike a pure product reference, its setting IS the ad's environment (see the ENVIRONMENT OVERRIDE below) — reproduce BOTH the featured product AND the place it is shown in, while still never copying the reference photo's exact composition, camera angle, framing, or camera characteristics.`;
    }
  }

  const preambleParts = [identityPreamble, productPreamble, envPreamble].filter(Boolean);
  const refLines = [...identityRefLines, ...productRefLines, ...envRefLines];

  let prompt = `${preambleParts.join("\n\n")}\n\n${basePrompt}\n\n${refLines.join("\n\n")}`;
  const extraContext =
    typeof args.additionalContext === "string" ? args.additionalContext.trim() : "";
  if (extraContext) {
    prompt = `${prompt}\n\nADDITIONAL CONTEXT: ${extraContext}`;
  }
  return prompt;
}

/**
 * Task #2665 — deterministic renderer boundary for exact person identity.
 *
 * Beta reference runs normally send the Sim-written prompt to the image model
 * verbatim. Identity preservation is a safety/meaning contract, not optional
 * creative guidance, so it is re-applied locally from the consented,
 * submit-time snapshot after Sim returns. Product and environment references
 * are deliberately excluded here and retain their existing Sim-only behavior.
 */
export function composePersonIdentityRendererPrompt(args: {
  basePrompt: string;
  refs: ReadonlyArray<ResolvedAdReference> | null | undefined;
}): string {
  const identityRefs = Array.isArray(args.refs)
    ? args.refs.filter(
        (ref) =>
          ref.purpose === "person_identity" &&
          ref.identityConsent === true,
      )
    : [];
  if (identityRefs.length === 0) return args.basePrompt;
  return composeReferenceInjectedPrompt({
    basePrompt: args.basePrompt,
    refs: identityRefs,
  });
}

export interface AdGenerationSelection {
  projectId?: string | null;
  personaId: string;
  promotedItemType: "product" | "offer";
  // True when promoting a non-physical Offer instead of a Product.
  isOfferMode: boolean;
  productId?: string | null;
  offerId?: string | null;
  // True when the user typed a free-form concept instead of picking a saved
  // narrative angle; the server transforms the concept into a saved angle.
  usingCustom: boolean;
  angleId?: string | null;
  concept: string;
  adUnitTypeIds: string[];
  aspectRatios: string[];
  salesDiscountId?: string | null;
  includeLogo: boolean;
  // Optional visual reference images + freeform context (Advanced settings).
  // Each reference may carry a purpose ("auto" | "environment" | "product");
  // absent/unknown collapses to "auto" in normalization.
  references?: ReadonlyArray<{ url?: unknown; note?: unknown; purpose?: unknown; identityConsent?: unknown }> | null;
  additionalContext?: string | null;
  // Task #744 — optional selected product variant (by name). null/"" = "no
  // specific variant" → byte-for-byte legacy behaviour. The server resolves the
  // name against the product's variantsJson and snapshots the full variant.
  selectedVariantName?: string | null;
  // Task #1413 — library-picked product reference images (KC-style, standard
  // runs only, beta-gated server-side). Click order matters: #1 is the
  // primary product photo the render attaches in the positional product slot.
  // Task #1426 — offer runs pass through too (sourced from the offer's
  // adgenImageRefs; same flag, same cap). Empty/absent → null in the payload
  // → the server stamps a null snapshot and the worker falls back to the
  // promoted item's own adgenImageRefs/mediaJson, byte-for-byte legacy.
  productImageUrls?: ReadonlyArray<string> | null;
  // Task #1536 — Google Font family for the run (beta-gated server-side on
  // the user's `google_font_ads` grant). Absent/empty → null in the
  // payload → the server stamps a null snapshot, byte-for-byte legacy.
  adFontFamily?: string | null;
  // Brand-gated ready persona character. Omitted entirely when the picker is
  // unavailable so disabled brands and meme selections preserve old payloads.
  selectedPersonaCharacterId?: string | null;
  // Task #1777 follow-up — per-run image engine choice. Absent/invalid means
  // the brand's configured default; never persist a UI default as a choice.
  imageEngineChoice?: AdsExpressImageEngineChoice | null;
}

export interface AdGenerationRequestBody {
  projectId: string | null;
  personaId: string;
  promotedItemType: "product" | "offer";
  productId: string | null;
  offerId: string | null;
  narrativeAngleId: string | null;
  customConcept: string | null;
  adUnitTypeIds: string[];
  aspectRatios: string[];
  salesDiscountId: string | null;
  includeLogo: boolean;
  // Always present in the payload (possibly empty/null) so the shape is stable;
  // the server treats [] / null as "no references" = legacy behaviour.
  references: AdReference[];
  additionalContext: string | null;
  // Task #744 — selected product variant name (null = no specific variant).
  selectedVariantName: string | null;
  // Task #1413 — library-picked product reference override (null = no explicit
  // selection → legacy product-photo resolution). Task #1426 — offer runs may
  // carry it too (validated against the offer's adgenImageRefs server-side).
  productImageUrls: string[] | null;
  // Task #1536 — Google Font family (null = no font → legacy render).
  adFontFamily: string | null;
  selectedPersonaCharacterId?: string;
  imageEngineChoice?: AdsExpressImageEngineChoice;
}

export function buildAdGenerationPayload(
  sel: AdGenerationSelection,
): AdGenerationRequestBody {
  return {
    projectId: sel.projectId || null,
    personaId: sel.personaId,
    promotedItemType: sel.promotedItemType,
    productId: sel.isOfferMode ? null : sel.productId ?? null,
    offerId: sel.isOfferMode ? sel.offerId ?? null : null,
    narrativeAngleId: sel.usingCustom ? null : sel.angleId ?? null,
    customConcept: sel.usingCustom ? sel.concept.trim() : null,
    adUnitTypeIds: sel.adUnitTypeIds,
    aspectRatios: sel.aspectRatios,
    salesDiscountId: sel.salesDiscountId || null,
    includeLogo: sel.includeLogo,
    references: normalizeAdReferences(sel.references, { standardAdsExpress: true }),
    additionalContext: normalizeAdditionalContext(sel.additionalContext),
    selectedVariantName: sel.isOfferMode
      ? null
      : (typeof sel.selectedVariantName === "string" && sel.selectedVariantName.trim()
          ? sel.selectedVariantName.trim()
          : null),
    productImageUrls: (() => {
      const urls = normalizeAdProductImageUrls(sel.productImageUrls);
      return urls.length > 0 ? urls : null;
    })(),
    adFontFamily:
      typeof sel.adFontFamily === "string" && sel.adFontFamily.trim()
        ? sel.adFontFamily.trim()
        : null,
    ...(typeof sel.selectedPersonaCharacterId === "string" &&
    sel.selectedPersonaCharacterId.trim()
      ? { selectedPersonaCharacterId: sel.selectedPersonaCharacterId.trim() }
      : {}),
    ...(isAdsExpressImageEngineChoice(sel.imageEngineChoice)
      ? { imageEngineChoice: sel.imageEngineChoice }
      : {}),
  };
}

// Task #1365 (superseded design) — variant-aware signal for Ads Express is
// injected INTO productInfo itself (the field every Sim prompt workflow
// already reads via <start.productInfo>), mirroring the proven ACTIVE
// PROMOTION / FEATURED PRODUCT OVERRIDE append-a-labeled-block pattern in
// ad-image-generator.ts. NO new Sim Start-block inputs, so no Sim workflow
// edits are required for this to function.
//
// Two PURE pieces, pin-tested:
//   1. stripVariantAwareProductInfoFields — when the snapshotted flag is ON,
//      the raw machine-y variant/media dumps (variantsJson, variantOptionsJson,
//      adgenImageRefs, mediaJson) are excluded from the object serialized into
//      baseProductInfo, so the copywriter sees ONLY the curated VARIANT
//      OVERRIDE block below, never the raw JSON-ish dump alongside it. The
//      caller gates on the flag: flag off = the original object untouched
//      (byte-identical productInfo).
//   2. composeVariantOverrideBlock — the data-only "VARIANT MODE: ON." stanza
//      appended to the SAME productInfo chain as ACTIVE PROMOTION / FEATURED
//      PRODUCT OVERRIDE, joined with " | " only when non-empty. Empty ("")
//      when the flag is off OR there is neither a selected variant nor any
//      variant names — those runs keep today's productInfo byte-for-byte.
//      The variant handling INSTRUCTIONS live in the user's Sim workflow
//      prompt, keyed off the stanza's labels (see the function body).
//
// SCOPE: both transforms apply ONLY to standard (non-KC, non-meme) runs.
// KC never reaches this ctPayload chain (separate Sim branch), but MEME ad
// units DO share it — variantAwareProductInfoActive is the single gate the
// generator uses at BOTH transform sites so a meme run's productInfo stays
// byte-for-byte identical even when the user's flag is granted (the meme Sim
// workflow writes an image prompt from an Imgflip template; a copywriter
// variant directive doesn't apply there and is explicitly out of scope).
export function variantAwareProductInfoActive(args: {
  enabled: boolean | null | undefined;
  isMemeAdUnit: boolean;
}): boolean {
  return args.enabled === true && !args.isMemeAdUnit;
}

// Task #1382 — the `beta_ad_prompts_enabled` flag opts a granted user's
// standard runs into the beta Sim workflow pair OUTRIGHT (no reference
// images or variant-aware mode required). PURE routing: a flag-only run
// sends the toggle-off reference extras (referenceImagesEnabled: false)
// and no VARIANT MODE stanza, so ONLY the workflow URL differs from a
// legacy run. Same meme exclusion as variantAwareProductInfoActive: meme
// ad units share the ctPayload chain but keep the original URLs even when
// the flag is granted; KC never reaches the chain at all.
export function betaAdPromptsRoutingActive(args: {
  enabled: boolean | null | undefined;
  isMemeAdUnit: boolean;
}): boolean {
  return args.enabled === true && !args.isMemeAdUnit;
}

export const VARIANT_AWARE_PRODUCT_INFO_EXCLUDED_KEYS = [
  "variantsJson",
  "variantOptionsJson",
  "adgenImageRefs",
  "mediaJson",
] as const;

export function stripVariantAwareProductInfoFields<T extends Record<string, unknown>>(
  product: T | null | undefined,
): Record<string, unknown> | null | undefined {
  if (!product || typeof product !== "object") return product;
  const out: Record<string, unknown> = { ...product };
  for (const key of VARIANT_AWARE_PRODUCT_INFO_EXCLUDED_KEYS) {
    delete out[key];
  }
  return out;
}

export function composeVariantOverrideBlock(args: {
  enabled: boolean | null | undefined;
  selectedVariantName?: string | null;
  variantsJson?: unknown;
}): string {
  if (args.enabled !== true) return "";
  const availableVariantNamesList = normalizeProductVariants(args.variantsJson)
    .map((v) => v.name.trim())
    .filter(Boolean);
  const selectedVariantNameForOverride =
    typeof args.selectedVariantName === "string" ? args.selectedVariantName.trim() : "";
  if (!selectedVariantNameForOverride && availableVariantNamesList.length === 0) return "";
  // Task #1367 (final design) — DATA-ONLY stanza. The user owns the variant
  // handling INSTRUCTIONS inside the Sim workflow prompt itself; our side
  // supplies only the facts, riding inside productInfo (which the workflows
  // already read via <start.productInfo>) so NO new Sim Start inputs are
  // needed. The literal "VARIANT MODE: ON." marker is the activation signal
  // the user's prompt rules key off — flag-off runs have no stanza at all
  // (byte-identical baseline), so the marker's presence == beta active with
  // variant data to apply. The three labels below (VARIANT MODE / SELECTED
  // VARIANT / VARIANT OPTIONS (CLOSED SET)) are a CONTRACT with the user's
  // Sim prompt wording — do not rename them without coordinating a prompt
  // update.
  const parts = ["VARIANT MODE: ON."];
  if (selectedVariantNameForOverride) {
    parts.push(`SELECTED VARIANT: "${selectedVariantNameForOverride}".`);
  }
  if (availableVariantNamesList.length > 0) {
    parts.push(`VARIANT OPTIONS (CLOSED SET): ${availableVariantNamesList.join(", ")}.`);
  }
  return parts.join(" ");
}
